import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../constants/providers/AuthProvider';

export interface PastClass {
  enrollment_id: string;
  class_id: string;
  checked_in_at: string | null;
  sport_name: string | null;
  location_name: string | null;
  instructor_names: string[] | null;
  start_time: string | null;
  end_time: string | null;
}

/**
 * Fetches the student's past attended classes with event details.
 * Joins class_enrollments → friendly_calendar_events via the class_id FK.
 */
export function usePastClasses(limit = 10) {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const query = useQuery({
    queryKey: ['past-classes', userId, limit],
    queryFn: async (): Promise<PastClass[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('class_enrollments')
        .select(`
          id,
          class_id,
          checked_in_at,
          friendly_calendar_events!inner (
            sport_name,
            location_name,
            instructor_names,
            start_time,
            end_time
          )
        `)
        .eq('student_id', userId)
        .eq('attended', true)
        .order('checked_in_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Flatten the joined data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data || []) as any[]).map((row) => {
        const event = row.friendly_calendar_events;
        return {
          enrollment_id: row.id,
          class_id: row.class_id,
          checked_in_at: row.checked_in_at,
          sport_name: event?.sport_name ?? null,
          location_name: event?.location_name ?? null,
          instructor_names: event?.instructor_names ?? null,
          start_time: event?.start_time ?? null,
          end_time: event?.end_time ?? null,
        };
      });
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    pastClasses: (query.data || []) as PastClass[],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
